import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { ContractVariable } from "./ContractEditor";

const defaultVariables: ContractVariable[] = [];

type ContractEditorContextType = {
  variables: ContractVariable[];
  addVariable: (newVariable: ContractVariable) => void;
  setVariables: (label: string, value: string) => void;
};
const ContractEditorContext = createContext<ContractEditorContextType>({
  variables: defaultVariables,
  addVariable: () => {},
  setVariables: () => {},
});

type ContractEditorProviderProps = { defaultState?: ContractVariable[] };

export const ContractEditorProvider: React.FC<ContractEditorProviderProps> = ({
  defaultState = [],
  children,
}) => {
  const [variables, setVariables] = useState<ContractVariable[]>(
    defaultState || defaultVariables
  );

  const addVariable = useCallback(
    (newVariable: ContractVariable) =>
      setVariables((v) => {
        if (!v.find((v) => v.label === newVariable.label)) v.push(newVariable);
        return [...v];
      }),
    []
  );

  const _setVariables = useCallback((label: string, value: string) => {
    setVariables((variables) =>
      [...variables].map((v) => {
        if (v.label === label) return { ...v, value };
        return v;
      })
    );
  }, []);

  const value = useMemo(
    () => ({ variables, addVariable, setVariables: _setVariables }),
    [variables]
  );

  return (
    <ContractEditorContext.Provider value={value}>
      {children}
    </ContractEditorContext.Provider>
  );
};

export function useContractEditorContext() {
  return useContext(ContractEditorContext);
}
